import { consolidationStateOf } from '../api/position';
import type { FxRate, Position, PositionSummary } from '../api/position';

/**
 * The portfolio's weight per currency, computed from `GET /position/summary`.
 *
 * The service sends each currency's NAV bucket (`navs`) and the consolidated total, but not
 * the bucket's share of that total. This ports b-cell-position-v1's conversion
 * (`util/consolidate.py` `convert_amount`, `util/value.py`) digit for digit, in integer
 * math, so each bucket converts to exactly the amount the service added into the total.
 * The port is checked on every read: the converted buckets must add up to the server's
 * `consolidated_nav.nav_amount` exactly, and when they do not, no weight is shown. A
 * weight that disagrees with the NAV card on the same screen is worse than none.
 */

/** b-cell-position-v1 `util/constant.py` `TOTAL_SCALE`: the scale a conversion composes at. */
const TOTAL_SCALE = 6;
/** b-cell-position-v1 `util/constant.py` `WEIGHT_DECIMALS`: decimals of a printed weight. */
const WEIGHT_DECIMALS = 2;

export interface CurrencyWeight {
  currency: string;
  /** The weight as the backend's `percent_string` would print it, e.g. `"36.90%"`. */
  weight: string | null;
  convertedAmount: bigint;
}

export type CurrencyAllocation =
  | { state: 'weights'; weights: CurrencyWeight[] }
  | { state: 'zero-nav' }
  | { state: 'unreconciled' }
  | { state: 'rate-missing' }
  | { state: 'pending' }
  | { state: 'no-buckets' };

function isAmount(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isSafeInteger(value);
}

function isScale(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isInteger(value) && value >= 0;
}

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(exponent);
}

/** `util/value.py` `_round_half_up`: half rounds away from zero. */
function roundHalfAwayFromZero(numerator: bigint, denominator: bigint): bigint {
  const negative = (numerator < 0n) !== (denominator < 0n);
  const magnitude = numerator < 0n ? -numerator : numerator;
  const divisor = denominator < 0n ? -denominator : denominator;
  const rounded = (2n * magnitude + divisor) / (2n * divisor);
  return negative ? -rounded : rounded;
}

function rescale(amount: bigint, scale: number, targetScale: number): bigint {
  if (targetScale >= scale) return amount * powerOfTen(targetScale - scale);
  return roundHalfAwayFromZero(amount, powerOfTen(scale - targetScale));
}

/** `util/value.py` `multiply` with `multiplier_exponent=0`, the only exponent a rate uses. */
function multiply(
  quantityAmount: bigint,
  quantityScale: number,
  priceAmount: bigint,
  priceScale: number,
): bigint {
  const rawAmount = quantityAmount * priceAmount;
  const exponent = quantityScale + priceScale - TOTAL_SCALE;
  if (exponent <= 0) return rawAmount * powerOfTen(-exponent);
  return roundHalfAwayFromZero(rawAmount, powerOfTen(exponent));
}

function divide(
  totalAmount: bigint,
  totalScale: number,
  rateAmount: bigint,
  rateScale: number,
): bigint | null {
  if (rateAmount === 0n) return null;
  const exponent = rateScale + TOTAL_SCALE - totalScale;
  if (exponent < 0) return roundHalfAwayFromZero(totalAmount, rateAmount * powerOfTen(-exponent));
  return roundHalfAwayFromZero(totalAmount * powerOfTen(exponent), rateAmount);
}

function readableRate(rate: FxRate | undefined): { amount: bigint; scale: number } | null {
  if (rate == null || !isAmount(rate.rate_amount) || !isScale(rate.rate_scale)) return null;
  return { amount: BigInt(rate.rate_amount), scale: rate.rate_scale };
}

/** `util/consolidate.py` `convert_amount`, at the target currency's scale. `null` = unconvertible. */
function convertAmount(
  totalAmount: bigint,
  totalScale: number,
  currency: string,
  targetCurrency: string,
  targetScale: number,
  rateBySymbol: Map<string, FxRate>,
): bigint | null {
  if (currency === targetCurrency || totalAmount === 0n) {
    return rescale(totalAmount, totalScale, targetScale);
  }
  const directRate = readableRate(rateBySymbol.get(`${currency}${targetCurrency}`));
  if (directRate !== null) {
    const composedAmount = multiply(totalAmount, totalScale, directRate.amount, directRate.scale);
    return rescale(composedAmount, TOTAL_SCALE, targetScale);
  }
  const inverseRate = readableRate(rateBySymbol.get(`${targetCurrency}${currency}`));
  if (inverseRate !== null) {
    const composedAmount = divide(totalAmount, totalScale, inverseRate.amount, inverseRate.scale);
    if (composedAmount === null) return null;
    return rescale(composedAmount, TOTAL_SCALE, targetScale);
  }
  return null;
}

/** `util/value.py` `percent_string`: basis points rounded half away from zero; `null` for a zero whole. */
function percentString(partAmount: bigint, wholeAmount: bigint): string | null {
  if (wholeAmount === 0n) return null;
  const basisPoints = roundHalfAwayFromZero(partAmount * powerOfTen(WEIGHT_DECIMALS + 2), wholeAmount);
  const sign = basisPoints < 0n ? '-' : '';
  const digits = (basisPoints < 0n ? -basisPoints : basisPoints)
    .toString()
    .padStart(WEIGHT_DECIMALS + 1, '0');
  return `${sign}${digits.slice(0, -WEIGHT_DECIMALS)}.${digits.slice(-WEIGHT_DECIMALS)}%`;
}

/**
 * Positions that carry a value but no currency. The service leaves them out of `navs` and
 * out of the NAV, so no weight of them exists; the card still names them, so they are
 * never silently missing. A position with no value is unpriced, not of unknown currency,
 * and the asset-class chart leaves it out the same way.
 */
export function hasUnknownCurrencyValue(positions: Position[]): boolean {
  return positions.some(
    (position) => position.total_amount !== null && position.total_amount !== undefined
      && (position.currency === null || position.currency === undefined),
  );
}

function compareWeights(a: CurrencyWeight, b: CurrencyWeight): number {
  if (a.convertedAmount !== b.convertedAmount) return a.convertedAmount > b.convertedAmount ? -1 : 1;
  return a.currency < b.currency ? -1 : a.currency > b.currency ? 1 : 0;
}

/** The per-currency weights, largest first, or the reason there are none. */
export function currencyAllocationOf(summary: PositionSummary | null | undefined): CurrencyAllocation {
  const navs = summary?.navs ?? [];
  if (!Array.isArray(navs)) return { state: 'unreconciled' };
  if (navs.length === 0) return { state: 'no-buckets' };

  const consolidationState = consolidationStateOf(summary);
  if (consolidationState === 'rate-missing') return { state: 'rate-missing' };
  if (consolidationState === 'unrepresentable') return { state: 'unreconciled' };
  if (consolidationState !== 'consolidated') return { state: 'pending' };

  const consolidated = summary!.consolidated_nav!;
  if (!isAmount(consolidated.nav_amount) || !isScale(consolidated.nav_scale)
    || !Array.isArray(consolidated.rates)) {
    return { state: 'unreconciled' };
  }
  const rateBySymbol = new Map(consolidated.rates.map((rate) => [rate?.symbol, rate]));
  const wholeAmount = BigInt(consolidated.nav_amount);

  const convertedBuckets: Array<{ currency: string; convertedAmount: bigint }> = [];
  for (const nav of navs) {
    if (!isAmount(nav?.nav_amount) || !isScale(nav?.nav_scale)) return { state: 'unreconciled' };
    const convertedAmount = convertAmount(
      BigInt(nav.nav_amount), nav.nav_scale, nav.currency,
      consolidated.currency, consolidated.nav_scale, rateBySymbol,
    );
    if (convertedAmount === null) return { state: 'unreconciled' };
    convertedBuckets.push({ currency: nav.currency, convertedAmount });
  }
  const convertedTotal = convertedBuckets.reduce((total, bucket) => total + bucket.convertedAmount, 0n);
  if (convertedTotal !== wholeAmount) return { state: 'unreconciled' };
  if (wholeAmount === 0n) return { state: 'zero-nav' };

  const weights = convertedBuckets.map((bucket) => ({
    ...bucket,
    weight: percentString(bucket.convertedAmount, wholeAmount),
  }));
  return { state: 'weights', weights: weights.sort(compareWeights) };
}
